// Catmull-Rom spline math + cubic Bézier helpers for pit lane ribbons.
//
// Catmull-Rom interpolation produces a smooth curve passing through all
// control points. We use it to turn hand-authored waypoints into a
// dense smooth centerline that's used for BOTH rendering and collision —
// you never drive on a polygon that looks like a curve.
//
// `sharp` flag on a waypoint mirrors the neighbor lookup so the tangent
// at that vertex comes from the chord direction (= visible kink).
//
// All functions are pure. Closed-loop variants wrap neighbor lookups;
// open-path variants mirror across the neighbor at endpoints.

// Catmull-Rom alpha:
//   0   = uniform     (tightest, can overshoot)
//   0.5 = centripetal (well-behaved, default — no self-intersections)
//   1.0 = chordal     (smoothest / loosest)
export function catmullRomPoint(p0, p1, p2, p3, t, alpha = 0.5) {
  const dist = (a, b) => Math.pow(Math.hypot(a.x - b.x, a.y - b.y), alpha);

  const t0 = 0;
  const t1 = t0 + dist(p0, p1);
  const t2 = t1 + dist(p1, p2);
  const t3 = t2 + dist(p2, p3);

  const tt = t1 + t * (t2 - t1);

  const safe = d => d || 1e-9;
  const lerp = (a, b, w) => ({ x: a.x + (b.x - a.x) * w, y: a.y + (b.y - a.y) * w });

  const A1 = lerp(p0, p1, (tt - t0) / safe(t1 - t0));
  const A2 = lerp(p1, p2, (tt - t1) / safe(t2 - t1));
  const A3 = lerp(p2, p3, (tt - t2) / safe(t3 - t2));
  const B1 = lerp(A1, A2, (tt - t0) / safe(t2 - t0));
  const B2 = lerp(A2, A3, (tt - t1) / safe(t3 - t1));
  return lerp(B1, B2, (tt - t1) / safe(t2 - t1));
}

// Single-point eval of a closed spline at (segmentIdx, t in [0, 1]).
// Honors the `sharp` flag by replacing a neighbor with the vertex itself.
export function curvePointAt(waypoints, segmentIdx, t, alpha = 0.5) {
  const n = waypoints.length;
  const p1 = waypoints[segmentIdx];
  const p2 = waypoints[(segmentIdx + 1) % n];
  const p0 = p1.sharp ? p1 : waypoints[(segmentIdx - 1 + n) % n];
  const p3 = p2.sharp ? p2 : waypoints[(segmentIdx + 2) % n];
  return catmullRomPoint(p0, p1, p2, p3, t, alpha);
}

// Open-path eval: at endpoints, mirror across the neighbor for natural
// continuation rather than wrapping to the other end.
export function curvePointAtOpen(waypoints, segmentIdx, t, alpha = 0.5) {
  const n = waypoints.length;
  const p1 = waypoints[segmentIdx];
  const p2 = waypoints[segmentIdx + 1];

  let p0;
  if (p1.sharp)              p0 = p1;
  else if (segmentIdx === 0) p0 = { x: 2 * p1.x - p2.x, y: 2 * p1.y - p2.y };
  else                       p0 = waypoints[segmentIdx - 1];

  let p3;
  if (p2.sharp)                  p3 = p2;
  else if (segmentIdx === n - 2) p3 = { x: 2 * p2.x - p1.x, y: 2 * p2.y - p1.y };
  else                           p3 = waypoints[segmentIdx + 2];

  return catmullRomPoint(p0, p1, p2, p3, t, alpha);
}

// Closed-loop densifier: track centerline, surface patches.
export function densifyClosedLoop(controls, subdivisions = 12, alpha = 0.5) {
  const out = [];
  for (let i = 0; i < controls.length; i++) {
    for (let j = 0; j < subdivisions; j++) {
      out.push(curvePointAt(controls, i, j / subdivisions, alpha));
    }
  }
  return out;
}

// Open-path densifier: walls when not closed, pit lane spline,
// curbs as open bands.
export function densifyOpenPath(controls, subdivisions = 12, alpha = 0.5) {
  if (controls.length < 2) return controls.slice();
  const out = [];
  for (let i = 0; i < controls.length - 1; i++) {
    for (let j = 0; j < subdivisions; j++) {
      out.push(curvePointAtOpen(controls, i, j / subdivisions, alpha));
    }
  }
  const last = controls[controls.length - 1];
  out.push({ x: last.x, y: last.y });
  return out;
}

// Curve tangent (radians) at (segmentIdx, t) via central difference.
// `closed` selects between curvePointAt and curvePointAtOpen.
export function curveHeadingAt(waypoints, segmentIdx, t, alpha = 0.5, closed = true) {
  const eps = 0.01;
  const evalFn = closed ? curvePointAt : curvePointAtOpen;
  const a = evalFn(waypoints, segmentIdx, Math.max(0.001, t - eps), alpha);
  const b = evalFn(waypoints, segmentIdx, Math.min(0.999, t + eps), alpha);
  return Math.atan2(b.y - a.y, b.x - a.x);
}

// Forward-tangent unit vector on a closed spline at (segmentIdx, t).
// Returns { x: 1, y: 0 } as a degenerate fallback for under-3 waypoints.
export function forwardTangentAt(waypoints, segmentIdx, t, alpha = 0.5) {
  if (waypoints.length < 3) return { x: 1, y: 0 };
  const heading = curveHeadingAt(waypoints, segmentIdx, t, alpha, true);
  return { x: Math.cos(heading), y: Math.sin(heading) };
}

// Endpoint unit tangents for an open spline. Sample two close points so
// alpha + sharp flag are respected. Returns null if the spline is too
// short or the samples coincide.
export function openSplineStartTangent(waypoints, alpha = 0.5) {
  if (waypoints.length < 2) return null;
  const p1 = curvePointAtOpen(waypoints, 0, 0,    alpha);
  const p2 = curvePointAtOpen(waypoints, 0, 0.02, alpha);
  const dx = p2.x - p1.x, dy = p2.y - p1.y;
  const len = Math.hypot(dx, dy);
  return len > 1e-9 ? { x: dx / len, y: dy / len } : null;
}

export function openSplineEndTangent(waypoints, alpha = 0.5) {
  if (waypoints.length < 2) return null;
  const lastSeg = waypoints.length - 2;
  const p1 = curvePointAtOpen(waypoints, lastSeg, 0.98, alpha);
  const p2 = curvePointAtOpen(waypoints, lastSeg, 1,    alpha);
  const dx = p2.x - p1.x, dy = p2.y - p1.y;
  const len = Math.hypot(dx, dy);
  return len > 1e-9 ? { x: dx / len, y: dy / len } : null;
}

// Effective width at (segmentIdx, t) — linearly interpolated between
// the surrounding waypoints. A waypoint with no explicit `width` field
// inherits the trackline default. This lets the user widen one corner
// or narrow a chicane without rebuilding the whole track.
//
// `closed` controls whether the segment after the last waypoint wraps
// back to the first. For open paths (pit lane), the last segment uses
// the second-to-last waypoint's width as both endpoints.
export function widthAt(waypoints, defaultWidth, segmentIdx, t, closed = true) {
  const n = waypoints.length;
  if (n === 0) return defaultWidth;
  const wp1 = waypoints[segmentIdx];
  const wp2 = closed
    ? waypoints[(segmentIdx + 1) % n]
    : (segmentIdx + 1 < n ? waypoints[segmentIdx + 1] : wp1);
  const w1 = wp1?.width ?? defaultWidth;
  const w2 = wp2?.width ?? defaultWidth;
  return w1 + (w2 - w1) * t;
}

// Build a closed-loop track's inner + outer edge polylines with
// variable width. Returns
//   { dense, inner, outer, halfWidths, subdivisions }
// where each entry has dense.length samples.
//
// `offsetPerSide` extends each side outward by a constant (in addition
// to the local width / 2). Used by the simulator's render to build a
// white edge band 0.3 m past the tarmac on each side without computing
// the perp twice. halfWidths in the result still reports the actual
// surface half-width, NOT the offset version.
export function buildVariableWidthEdgesClosed(waypoints, defaultWidth, alpha, subdivisions = 14, offsetPerSide = 0) {
  const dense = densifyClosedLoop(waypoints, subdivisions, alpha);
  const inner = [], outer = [], halfWidths = [];
  for (let i = 0; i < dense.length; i++) {
    const a = dense[i];
    const b = dense[(i + 1) % dense.length];
    const dx = b.x - a.x, dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    const segIdx = Math.floor(i / subdivisions);
    const t = (i % subdivisions) / subdivisions;
    const half = widthAt(waypoints, defaultWidth, segIdx, t, true) / 2;
    halfWidths.push(half);
    if (len < 1e-9) {
      inner.push({ x: a.x, y: a.y });
      outer.push({ x: a.x, y: a.y });
      continue;
    }
    const nx = -dy / len, ny = dx / len;
    const offsetHalf = half + offsetPerSide;
    inner.push({ x: a.x - nx * offsetHalf, y: a.y - ny * offsetHalf });
    outer.push({ x: a.x + nx * offsetHalf, y: a.y + ny * offsetHalf });
  }
  return { dense, inner, outer, halfWidths, subdivisions };
}

// Open-path variant of buildVariableWidthEdgesClosed. Used by the pit
// lane (a non-closed spline) so its width can vary per-waypoint just
// like the main track. Returns the same { dense, inner, outer,
// halfWidths, subdivisions } shape; halfWidths[i] is the half-width at
// dense[i], computed from the segment that point lies in.
export function buildVariableWidthEdgesOpen(waypoints, defaultWidth, alpha, subdivisions = 14, offsetPerSide = 0) {
  if (waypoints.length < 2) {
    return { dense: [], inner: [], outer: [], halfWidths: [], subdivisions };
  }
  const dense = densifyOpenPath(waypoints, subdivisions, alpha);
  const inner = [], outer = [], halfWidths = [];
  for (let i = 0; i < dense.length; i++) {
    const a = dense[i];
    const b = i + 1 < dense.length ? dense[i + 1] : null;
    let nx = 0, ny = 1;
    if (b) {
      const dx = b.x - a.x, dy = b.y - a.y;
      const len = Math.hypot(dx, dy);
      if (len > 1e-9) { nx = -dy / len; ny = dx / len; }
    } else if (i > 0) {
      const prev = dense[i - 1];
      const dx = a.x - prev.x, dy = a.y - prev.y;
      const len = Math.hypot(dx, dy);
      if (len > 1e-9) { nx = -dy / len; ny = dx / len; }
    }
    let segIdx, t;
    if (i >= (waypoints.length - 1) * subdivisions) {
      segIdx = waypoints.length - 2;
      t = 1;
    } else {
      segIdx = Math.floor(i / subdivisions);
      t = (i % subdivisions) / subdivisions;
    }
    const half = widthAt(waypoints, defaultWidth, segIdx, t, false) / 2;
    halfWidths.push(half);
    const offsetHalf = half + offsetPerSide;
    inner.push({ x: a.x - nx * offsetHalf, y: a.y - ny * offsetHalf });
    outer.push({ x: a.x + nx * offsetHalf, y: a.y + ny * offsetHalf });
  }
  return { dense, inner, outer, halfWidths, subdivisions };
}

// Cubic Bézier sample. Used to build pit lane merge ribbons between the
// pit lane spline endpoints and the entry/exit on the main racing line.
export function cubicBezierPoint(P0, P1, P2, P3, t) {
  const u = 1 - t, u2 = u * u, u3 = u2 * u;
  const t2 = t * t, t3 = t2 * t;
  return {
    x: u3*P0.x + 3*u2*t*P1.x + 3*u*t2*P2.x + t3*P3.x,
    y: u3*P0.y + 3*u2*t*P1.y + 3*u*t2*P2.y + t3*P3.y,
  };
}

export function densifyCubicBezier(P0, P1, P2, P3, subs = 28) {
  const out = [];
  for (let i = 0; i <= subs; i++) out.push(cubicBezierPoint(P0, P1, P2, P3, i / subs));
  return out;
}

// Dense Bézier samples for one pit lane merge ribbon. Tangent control
// distance is chord/3 with 28 subdivisions — matches the editor's
// drawTransitionRibbon so visuals + the car's surface lookup agree on
// where the tarmac actually is.
export function buildRibbonDense(P0, T0, P3, T3) {
  const chord = Math.hypot(P3.x - P0.x, P3.y - P0.y);
  if (chord < 0.5) return null;
  const scale = chord / 3;
  const P1 = { x: P0.x + T0.x * scale, y: P0.y + T0.y * scale };
  const P2 = { x: P3.x - T3.x * scale, y: P3.y - T3.y * scale };
  return densifyCubicBezier(P0, P1, P2, P3, 28);
}
